import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ConfigProvider } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { StyledDateRangePicker, PickerContainer } from './styles';
import { DateRangePickerProps } from './types';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  selectedDateRange,
  onDateRangeChange,
  placeholder = 'Select date range',
  size = 'middle',
  style,
  className,
  enforceSequentialSelection = false,
}) => {
  const { isDarkMode } = useTheme();
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [hasStart, setHasStart] = useState(false);
  const [showEndTooltip, setShowEndTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedDateRange?.startDate && selectedDateRange?.endDate) {
      let startDate: Date;
      let endDate: Date;

      if (moment.isMoment(selectedDateRange.startDate)) {
        startDate = selectedDateRange.startDate.toDate();
      } else if (selectedDateRange.startDate instanceof Date) {
        startDate = selectedDateRange.startDate;
      } else {
        startDate = new Date(selectedDateRange.startDate);
      }

      if (moment.isMoment(selectedDateRange.endDate)) {
        endDate = selectedDateRange.endDate.toDate();
      } else if (selectedDateRange.endDate instanceof Date) {
        endDate = selectedDateRange.endDate;
      } else {
        endDate = new Date(selectedDateRange.endDate);
      }

      const start = dayjs(startDate);
      const end = dayjs(endDate);
      if (enforceSequentialSelection) setHasStart(true);
      setDates([start, end]);
    } else {
      if (enforceSequentialSelection) setHasStart(false);
      setDates(null);
    }
  }, [selectedDateRange, enforceSequentialSelection]);

  const handleChange = (values: [Dayjs | null, Dayjs | null] | null) => {
    if (values && values[0] && values[1]) {
      const startDate = values[0].toDate();
      const endDate = values[1].toDate();
      onDateRangeChange({
        startDate: moment(startDate),
        endDate: moment(endDate),
      });
    } else {
      if (enforceSequentialSelection) setHasStart(false);
      onDateRangeChange({
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleCalendarChange = (values: [Dayjs | null, Dayjs | null] | null) => {
    if (enforceSequentialSelection) setHasStart(!!(values?.[0]));
  };

  const antdTheme = getAntdThemeConfig(isDarkMode);

  return (
    <ConfigProvider theme={antdTheme}>
      <PickerContainer className={className} style={style}>
        <StyledDateRangePicker
          value={dates}
          onChange={handleChange}
          onCalendarChange={enforceSequentialSelection ? handleCalendarChange : undefined}
          format="MMM DD, YYYY"
          placeholder={[placeholder, placeholder]}
          size={size}
          suffixIcon={<FontAwesomeIcon icon={faCalendarAlt} />}
          allowClear
          inputReadOnly={enforceSequentialSelection}
        />
        {enforceSequentialSelection && !hasStart && (
          <div
            ref={overlayRef}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '50%',
              height: '100%',
              cursor: 'not-allowed',
              zIndex: 2,
            }}
            onMouseEnter={() => {
              if (overlayRef.current) {
                const rect = overlayRef.current.getBoundingClientRect();
                setTooltipPos({
                  top: rect.top + window.scrollY - 8,
                  left: rect.left + rect.width / 2 + window.scrollX,
                });
                setShowEndTooltip(true);
              }
            }}
            onMouseLeave={() => setShowEndTooltip(false)}
          />
        )}
        {showEndTooltip && ReactDOM.createPortal(
          <div
            style={{
              position: 'absolute',
              top: tooltipPos.top,
              left: tooltipPos.left,
              transform: 'translate(-50%, -100%)',
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: '#fff',
              padding: '6px 10px',
              borderRadius: '6px',
              fontSize: '14px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 99999,
            }}
          >
            Please select start date first
          </div>,
          document.body
        )}
      </PickerContainer>
    </ConfigProvider>
  );
};

export default DateRangePicker;
